import type { MapTiles } from '@gardener/protocol';

/**
 * The Maps page's view (ADR-0223): the world point at the canvas' middle and how many CSS px a world px is there. Pure:
 * `maps.tsx` draws with it.
 */
export interface View {
  x: number;
  y: number;
  scale: number;
}

/** What a 1080p player's screen shows of the world (the client's `TARGET_VIEW_W` × `TARGET_VIEW_H`). */
export const PLAYER_VIEW = { w: 480, h: 270 } as const;
/** The closest the view goes (CSS px a world px: a cell 1024 px): as good as endless for pixel art. */
export const MAX_SCALE = 64;

/** The whole map in a `w` × `h` canvas, a little air round it. */
export const fit = (m: Pick<MapTiles, 'width' | 'height'>, w: number, h: number): View => ({
  x: m.width / 2,
  y: m.height / 2,
  scale: Math.min(w / m.width, h / m.height) * 0.95,
});

/** A player's screen (`PLAYER_VIEW`) filling the canvas round world (x, y). */
export const playerView = (x: number, y: number, w: number, h: number): View => ({
  x,
  y,
  scale: Math.min(w / PLAYER_VIEW.w, h / PLAYER_VIEW.h),
});

export const toWorld = (v: View, w: number, h: number, sx: number, sy: number) => ({
  x: v.x + (sx - w / 2) / v.scale,
  y: v.y + (sy - h / 2) / v.scale,
});

export const toScreen = (v: View, w: number, h: number, wx: number, wy: number) => ({
  x: (wx - v.x) * v.scale + w / 2,
  y: (wy - v.y) * v.scale + h / 2,
});

/** The view zoomed by `factor` about screen point (sx, sy), which stays over the same world point; `min` the farthest. */
export function zoomAt(v: View, w: number, h: number, sx: number, sy: number, factor: number, min: number): View {
  const scale = Math.max(min, Math.min(MAX_SCALE, v.scale * factor));
  const at = toWorld(v, w, h, sx, sy);
  return { x: at.x - (sx - w / 2) / scale, y: at.y - (sy - h / 2) / scale, scale };
}

/** Tile px a world px at level `z` (the top one: the art's density). */
export const densityOf = (m: Pick<MapTiles, 'density' | 'levels'>, z: number): number =>
  m.density / 2 ** (m.levels - 1 - z);

/** The coarsest level as sharp as the screen (`px` device px a world px), or the top one past it. */
export function levelFor(m: Pick<MapTiles, 'density' | 'levels'>, px: number): number {
  for (let z = 0; z < m.levels; z++) if (densityOf(m, z) >= px) return z;
  return m.levels - 1;
}

/** Level `z`'s tiles in view (column and row ranges, ends excluded), clipped to the map. */
export function tilesInView(m: MapTiles, z: number, v: View, w: number, h: number) {
  const span = m.size / densityOf(m, z);
  const a = toWorld(v, w, h, 0, 0);
  const b = toWorld(v, w, h, w, h);
  const clamp = (n: number, end: number) => Math.max(0, Math.min(Math.ceil(end / span), n));
  return {
    x0: clamp(Math.floor(a.x / span), m.width),
    y0: clamp(Math.floor(a.y / span), m.height),
    x1: clamp(Math.ceil(b.x / span), m.width),
    y1: clamp(Math.ceil(b.y / span), m.height),
    span,
  };
}

/** A pen's line thinned (Ramer-Douglas-Peucker): no point of it off the result by more than `tolerance`. */
export function simplify(points: readonly [number, number][], tolerance: number): [number, number][] {
  if (points.length < 3) return [...points];
  const [ax, ay] = points[0] as [number, number];
  const [bx, by] = points[points.length - 1] as [number, number];
  const len = Math.hypot(bx - ax, by - ay);
  let [far, at] = [0, 0];
  for (let i = 1; i < points.length - 1; i++) {
    const [px, py] = points[i] as [number, number];
    const d = len ? Math.abs((bx - ax) * (ay - py) - (ax - px) * (by - ay)) / len : Math.hypot(px - ax, py - ay);
    if (d > far) [far, at] = [d, i];
  }
  if (far <= tolerance) return [points[0] as [number, number], points[points.length - 1] as [number, number]];
  return [...simplify(points.slice(0, at + 1), tolerance).slice(0, -1), ...simplify(points.slice(at), tolerance)];
}
